import { useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT } from '@/shared/i18n'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import {
  MAX_FILE_MB,
  extractPdfPages,
  getPdfPageCount,
  parsePageRanges,
  splitFilename,
  validateFileSize,
} from './lib'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'
import { RevealOnMount } from '@/shared/ui/RevealOnMount'

const UTILITY = getUtilityDefinition('splitPdf').analyticsKey

type Phase =
  | { kind: 'idle' }
  | { kind: 'inspecting'; file: File }
  | { kind: 'ready'; file: File; totalPages: number }
  | { kind: 'loading'; file: File; totalPages: number }
  | {
      kind: 'success'
      file: File
      blob: Blob
      objectUrl: string
      selectedPages: number
      totalPages: number
    }
  | { kind: 'error'; file: File | null; message: string; errorCode: string }

interface DropZoneProps {
  onFile: (file: File) => void
  disabled: boolean
}

function DropZone({ onFile, disabled }: DropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) return
    onFile(file)
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t.splitPdf.pickLabel}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        if (!disabled) handleFiles(e.dataTransfer.files)
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-3',
        'rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'cursor-pointer select-none transition-colors',
        dragging && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-accent/30',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-primary/60"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>

      <div>
        <p className="text-sm font-medium text-foreground">
          <FilePickPrompt drag={t.common.dragPdf} />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {format(t.common.pdfLimit, { size: MAX_FILE_MB })}
        </p>
        <p className="mt-1 flex items-center justify-center gap-1 text-[11px] text-muted-foreground/80">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          {t.common.localBadge}
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => handleFiles(e.target.files)}
        disabled={disabled}
      />
    </div>
  )
}

interface FileSummaryProps {
  file: File
  totalPages: number
  onClear: () => void
  disabled: boolean
}

function FileSummary({ file, totalPages, onClear, disabled }: FileSummaryProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-accent/30 px-4 py-3">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0 text-primary"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
        <p className="text-xs text-muted-foreground">
          {formatBytes(file.size)} · {format(t.splitPdf.totalPages, { pages: totalPages })}
        </p>
      </div>
      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={t.common.deleteFile}
        className={cn(
          'rounded p-1 text-muted-foreground transition-colors',
          'hover:bg-destructive/10 hover:text-destructive',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  )
}

interface ResultPanelProps {
  file: File
  blob: Blob
  objectUrl: string
  selectedPages: number
  totalPages: number
  onReset: () => void
}

function ResultPanel({
  file,
  blob,
  objectUrl,
  selectedPages,
  totalPages,
  onReset,
}: ResultPanelProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  function handleDownload() {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = splitFilename(file.name)
    a.click()
  }

  return (
    <Card className="border-green-200 bg-green-50/50 dark:border-green-500/30 dark:bg-green-500/10">
      <CardContent className="p-5">
        <RevealOnMount />
        <div className="mb-4 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-green-600 dark:text-green-300"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className="text-base font-semibold text-green-700 dark:text-green-200">{t.common.done}</span>
        </div>

        <div className="mb-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.splitPdf.selectedPages}</span>
            <span className="font-medium text-foreground">
              {selectedPages} / {totalPages}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.size}</span>
            <span className="font-medium text-foreground">{formatBytes(blob.size)}</span>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button onClick={handleDownload} size="lg" className="flex-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {t.common.download}
          </Button>
          <Button variant="outline" onClick={onReset} className="flex-1 sm:flex-none">
            {t.splitPdf.again}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

interface ErrorPanelProps {
  message: string
  onRetry: () => void
}

function ErrorPanel({ message, onRetry }: ErrorPanelProps) {
  const t = useT()

  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-destructive"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="15" x2="12.01" y2="15" />
          </svg>
          <span className="text-sm font-semibold text-destructive">{t.common.errorTitle}</span>
        </div>
        <p className="mb-4 text-sm text-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      </CardContent>
    </Card>
  )
}

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

export function RazdelitPdfForm() {
  const t = useT()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [ranges, setRanges] = useState('1')

  const isBusy = phase.kind === 'inspecting' || phase.kind === 'loading'
  const readyPhase =
    phase.kind === 'ready' || phase.kind === 'loading' || phase.kind === 'success'
      ? phase
      : null
  const parsed =
    readyPhase && phase.kind !== 'success'
      ? parsePageRanges(ranges, readyPhase.totalPages)
      : null
  const selectedPages = parsed?.ok ? parsed.pageIndices.length : 0

  function cleanupSuccessUrl() {
    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }
  }

  async function handleFile(file: File) {
    cleanupSuccessUrl()

    if (!validateFileSize(file)) {
      setPhase({
        kind: 'error',
        file,
        errorCode: 'client_too_large',
        message: format(t.common.tooLarge, { size: MAX_FILE_MB }),
      })
      return
    }

    setPhase({ kind: 'inspecting', file })

    try {
      const totalPages = await getPdfPageCount(file)
      setRanges(totalPages > 1 ? `1-${totalPages}` : '1')
      setPhase({ kind: 'ready', file, totalPages })
    } catch (err: unknown) {
      const code = err instanceof Error && err.message.toLowerCase().includes('encrypted')
        ? 'encrypted'
        : 'parse_error'
      setPhase({
        kind: 'error',
        file,
        errorCode: code,
        message: code === 'encrypted' ? t.splitPdf.encrypted : t.splitPdf.parseError,
      })
    }
  }

  function handleClear() {
    cleanupSuccessUrl()
    setRanges('1')
    setPhase({ kind: 'idle' })
  }

  function handleRetry() {
    if (phase.kind === 'error' && phase.file) {
      void handleFile(phase.file)
      return
    }
    handleClear()
  }

  async function handleSubmit() {
    if (phase.kind !== 'ready') return
    const parsedRanges = parsePageRanges(ranges, phase.totalPages)
    if (!parsedRanges.ok) {
      toolError(UTILITY, parsedRanges.code, { total_pages: phase.totalPages })
      return
    }

    const { file, totalPages } = phase
    setPhase({ kind: 'loading', file, totalPages })
    toolInvoke(UTILITY, {
      input_bytes: file.size,
      total_pages: totalPages,
      selected_pages: parsedRanges.pageIndices.length,
    })

    const startMs = Date.now()

    try {
      const { blob, selectedPages: extractedPages } = await extractPdfPages(
        file,
        parsedRanges.pageIndices,
      )
      const durationMs = Date.now() - startMs
      const objectUrl = URL.createObjectURL(blob)

      toolSuccess(UTILITY, durationMs, {
        input_bytes: file.size,
        output_bytes: blob.size,
        total_pages: totalPages,
        selected_pages: extractedPages,
      })

      setPhase({
        kind: 'success',
        file,
        blob,
        objectUrl,
        selectedPages: extractedPages,
        totalPages,
      })
    } catch (err: unknown) {
      const code = err instanceof Error && err.message.toLowerCase().includes('encrypted')
        ? 'encrypted'
        : 'parse_error'
      toolError(UTILITY, code, { total_pages: totalPages })
      setPhase({
        kind: 'error',
        file,
        errorCode: code,
        message: code === 'encrypted' ? t.splitPdf.encrypted : t.splitPdf.parseError,
      })
    }
  }

  if (phase.kind === 'success') {
    return (
      <ResultPanel
        file={phase.file}
        blob={phase.blob}
        objectUrl={phase.objectUrl}
        selectedPages={phase.selectedPages}
        totalPages={phase.totalPages}
        onReset={handleClear}
      />
    )
  }

  return (
    <div className="space-y-5">
      {phase.kind === 'ready' || phase.kind === 'loading' ? (
        <div className="space-y-4">
          <FileSummary
            file={phase.file}
            totalPages={phase.totalPages}
            onClear={handleClear}
            disabled={isBusy}
          />

          <div className="space-y-2">
            <label htmlFor="split-pdf-ranges" className="text-sm font-medium text-foreground">
              {t.splitPdf.rangesLabel}
            </label>
            <input
              id="split-pdf-ranges"
              type="text"
              value={ranges}
              onChange={(e) => setRanges(e.target.value)}
              disabled={isBusy}
              placeholder={t.splitPdf.rangesPlaceholder}
              aria-label={t.splitPdf.rangesAria}
              className={cn(
                'w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
                parsed && !parsed.ok && 'border-destructive focus:ring-destructive/30',
              )}
            />
            <p className="text-xs text-muted-foreground">
              {t.splitPdf.rangesHintPrefix}
              <span className="font-mono">1-3, 7, 10-12</span>
              {t.splitPdf.rangesHintSuffix}
            </p>
          </div>

          <div className="rounded-lg border bg-accent/30 px-4 py-3 text-sm">
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">{t.splitPdf.totalPagesLabel}</span>
              <span className="font-medium text-foreground">{phase.totalPages}</span>
            </div>
            <div className="mt-1 flex justify-between gap-2">
              <span className="text-muted-foreground">{t.splitPdf.selectedPages}</span>
              <span className="font-medium text-foreground">{selectedPages}</span>
            </div>
          </div>

          {parsed && !parsed.ok && (
            <p className="text-sm text-destructive">
              {parsed.code === 'empty'
                ? t.splitPdf.emptyRanges
                : parsed.code === 'out_of_range'
                  ? format(t.splitPdf.outOfRange, { pages: phase.totalPages })
                  : parsed.code === 'reversed_range'
                    ? t.splitPdf.reversedRange
                    : t.splitPdf.invalidRanges}
            </p>
          )}
        </div>
      ) : (
        <DropZone onFile={handleFile} disabled={isBusy} />
      )}

      {phase.kind === 'inspecting' && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Spinner />
          {t.splitPdf.inspecting}
        </div>
      )}

      {phase.kind === 'error' && <ErrorPanel message={phase.message} onRetry={handleRetry} />}

      <Button
        size="lg"
        className="w-full"
        disabled={phase.kind !== 'ready' || !parsed?.ok || isBusy}
        onClick={handleSubmit}
      >
        {phase.kind === 'loading' ? (
          <>
            <Spinner />
            {t.splitPdf.loading}
          </>
        ) : (
          t.splitPdf.submit
        )}
      </Button>
    </div>
  )
}
